import { graphql } from "../../../../__generated__/gql";
import type {
  AccommodationOnRoomDetailContentFragment,
  RoomTypeOnRoomDetailContentFragment,
} from "../../../../__generated__/gql/graphql";
import { buildRoomTitle } from "../../../utils/build-room-title";
import type { RoomDetailView } from "../content/room-detail-content";

import { buildIndoorEquipmentText } from "./build-indoor-equipment-text";

// この2つの fragment は描画コンポーネント (RoomDetailContent) ではなくここ (_utils) に置いている。
// RoomDetailContent は正規化済み RoomDetailView を描画する設計で fragment を直接消費せず、
// fragment の実際の消費者は下記の mapper のため。fragment と mapper を同じ境界モジュールに
// colocate し、テスト可能な1単位に保つ意図。
export const AccommodationOnRoomDetailContent = graphql(`
  fragment AccommodationOnRoomDetailContent on Accommodation {
    code
    site {
      id
      name
      content {
        id
        googleMapsUrl
      }
    }
    roomType {
      id
      displayName
      notes
      features {
        id
        slug
        displayName
        iconSlug
        sort
      }
      content {
        id
        heroImages {
          rawUrl
        }
      }
    }
    architectureType {
      id
      name
      content {
        id
        capacityDescription
        sizeDescription
        equipmentsListUrl
        basicAmenities {
          name
        }
      }
    }
  }
`);

export const RoomTypeOnRoomDetailContent = graphql(`
  fragment RoomTypeOnRoomDetailContent on RoomType {
    displayName
    notes
    features {
      id
      slug
      displayName
      iconSlug
      sort
    }
    content {
      id
      heroImages {
        rawUrl
      }
    }
    site {
      id
      name
      content {
        id
        googleMapsUrl
      }
    }
    architectureType {
      id
      name
      content {
        id
        capacityDescription
        sizeDescription
        equipmentsListUrl
        basicAmenities {
          name
        }
      }
    }
  }
`);

/**
 * Accommodation 形（部屋番号あり）→ 正規化 DTO。
 * 入力は unmask 済みの fragment データ（呼び出し側の component が `useFragment` で剥がす）。
 */
export function accommodationToRoomDetailView(
  accommodation: AccommodationOnRoomDetailContentFragment,
): RoomDetailView {
  const { code, site, roomType, architectureType } = accommodation;

  const bigTitle = buildRoomTitle({
    siteName: site.name,
    architectureName: architectureType.name,
    roomCode: code,
  });

  return {
    heroImages:
      roomType?.content?.heroImages.map((img) => ({ url: img.rawUrl })) ?? [],
    altText: bigTitle,
    bigTitle,
    subtitle: roomType?.displayName ?? null,
    notes: roomType?.notes ?? null,
    capacityDescription: architectureType.content.capacityDescription || null,
    sizeDescription: architectureType.content.sizeDescription || null,
    indoorEquipment:
      buildIndoorEquipmentText(architectureType.content.basicAmenities) ?? null,
    features: roomType?.features ?? [],
    equipmentsListUrl: architectureType.content.equipmentsListUrl ?? null,
    googleMapsUrl: site.content.googleMapsUrl ?? null,
  };
}

/**
 * RoomType 形（部屋番号なし）→ 正規化 DTO。
 * 入力は unmask 済みの fragment データ（呼び出し側の component が `useFragment` で剥がす）。
 */
export function roomTypeToRoomDetailView(
  roomType: RoomTypeOnRoomDetailContentFragment,
): RoomDetailView {
  const { displayName, notes, features, content, site, architectureType } =
    roomType;

  // RoomType.architectureType は nullable（建築タイプ未設定の部屋タイプがありうる）。
  // その場合は定員・広さ・室内設備・備品 URL を出さない。
  const bigTitle = buildRoomTitle({
    siteName: site.name,
    architectureName: architectureType?.name ?? "",
  }).trim();

  return {
    heroImages: content?.heroImages.map((img) => ({ url: img.rawUrl })) ?? [],
    altText: bigTitle,
    bigTitle,
    subtitle: displayName || null,
    notes: notes ?? null,
    capacityDescription: architectureType?.content.capacityDescription || null,
    sizeDescription: architectureType?.content.sizeDescription || null,
    indoorEquipment: architectureType
      ? (buildIndoorEquipmentText(architectureType.content.basicAmenities) ??
        null)
      : null,
    features,
    equipmentsListUrl: architectureType?.content.equipmentsListUrl ?? null,
    googleMapsUrl: site.content.googleMapsUrl ?? null,
  };
}
